// How people can steer a rewrite, and the kinds of change explanations name. Dependency-free,
// like text.ts, so every client can use it cheaply.

export const TONES = [
  'natural',
  'friendly',
  'casual',
  'formal',
  'confident',
  'empathetic',
  'persuasive',
  'diplomatic',
  'direct',
  'polite',
  'enthusiastic',
  'inspiring',
  'playful',
  'academic',
  'objective',
] as const;
export type Tone = (typeof TONES)[number];

/**
 * The built-in tones the Tone menu shows as every writer starts: the few most writing needs. The
 * rest start hidden, keeping the menu short, until the writer shows them in Manage tones.
 */
export const COMMON_TONES: readonly Tone[] = ['natural', 'friendly', 'casual', 'formal'];

export const TONE_LABELS: Readonly<Record<Tone, string>> = {
  natural: 'As written',
  friendly: 'Friendly',
  casual: 'Casual',
  formal: 'Formal',
  confident: 'Confident',
  empathetic: 'Empathetic',
  persuasive: 'Persuasive',
  diplomatic: 'Diplomatic',
  direct: 'Direct',
  polite: 'Polite',
  enthusiastic: 'Enthusiastic',
  inspiring: 'Inspiring',
  playful: 'Playful',
  academic: 'Academic',
  objective: 'Objective',
};

export const LENGTHS = ['same', 'shorter', 'longer'] as const;
export type Length = (typeof LENGTHS)[number];

export const LENGTH_LABELS: Readonly<Record<Length, string>> = {
  same: 'Same length',
  shorter: 'Shorter',
  longer: 'Longer',
};

/** "auto" keeps each text in its own language; the rest are ISO 639-1 codes to write in. */
export const LANGUAGES = [
  'auto',
  'en',
  'es',
  'fr',
  'de',
  'it',
  'pt',
  'nl',
  'pl',
  'uk',
  'cs',
  'sv',
  'da',
  'no',
  'fi',
  'ro',
  'el',
  'tr',
  'ar',
  'he',
  'hi',
  'id',
  'vi',
  'th',
  'ja',
  'ko',
  'zh',
] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_NAMES: Readonly<Record<Language, string>> = {
  auto: 'Same as my text',
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  pl: 'Polish',
  uk: 'Ukrainian',
  cs: 'Czech',
  sv: 'Swedish',
  da: 'Danish',
  no: 'Norwegian',
  fi: 'Finnish',
  ro: 'Romanian',
  el: 'Greek',
  tr: 'Turkish',
  ar: 'Arabic',
  he: 'Hebrew',
  hi: 'Hindi',
  id: 'Indonesian',
  vi: 'Vietnamese',
  th: 'Thai',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese (Simplified)',
};

/**
 * Longest free-form instruction, such as "make it persuasive for a client": as long as a saved
 * one, so one typed for a rewrite can be saved as it is.
 */
export const MAX_INSTRUCTION_LENGTH = 500;

export interface RewriteOptions {
  readonly tone: Tone;
  /** The id of one of the writer's custom tones, used in place of `tone`. */
  readonly customTone?: string | undefined;
  readonly length: Length;
  readonly language: Language;
  /** Extra guidance in the writer's words. */
  readonly instruction?: string | undefined;
  /** The ids of the writer's saved instructions to follow too. */
  readonly savedInstructions?: readonly string[] | undefined;
}

export const DEFAULT_REWRITE_OPTIONS: RewriteOptions = {
  tone: 'natural',
  length: 'same',
  language: 'auto',
};

export const CHANGE_KINDS = [
  'grammar',
  'spelling',
  'punctuation',
  'word-choice',
  'clarity',
  'concision',
  'tone',
  'structure',
  'style',
  'translation',
  'other',
] as const;
export type ChangeKind = (typeof CHANGE_KINDS)[number];

export const CHANGE_KIND_LABELS: Readonly<Record<ChangeKind, string>> = {
  grammar: 'Grammar',
  spelling: 'Spelling',
  punctuation: 'Punctuation',
  'word-choice': 'Word choice',
  clarity: 'Clarity',
  concision: 'Concision',
  tone: 'Tone',
  structure: 'Structure',
  style: 'Style',
  translation: 'Translation',
  other: 'Other',
};
